/**
 * Receipt printing utilities.
 *
 * Two approaches:
 *   1. Legacy: RECEIPT_PRINT_PAGE_STYLE + executeReceiptPrint (used by react-to-print)
 *   2. Template: printReceiptFromTemplate — builds a self-contained HTML doc,
 *      injects it into a hidden iframe, and triggers window.print().
 *      This guarantees pixel-perfect half-A4 output regardless of screen styles.
 */

// ─── Legacy approach (kept for backward compat with react-to-print) ──────────

export const RECEIPT_PRINT_PAGE_STYLE = `
    @page {
        size: A4 portrait;
        margin: 0mm !important;
    }

    @media print {
        * { box-sizing: border-box; }

        html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 210mm !important;
            height: 297mm !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
        }

        body > * {
            margin: 0 !important;
            padding: 0 !important;
        }

        .receipt-half-page {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 210mm !important;
            height: 148.5mm !important;
            max-height: 148.5mm !important;
            margin: 0 !important;
            padding: 2mm 5mm !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
            transform: none !important;
            box-shadow: none !important;
            border: none !important;
            border-radius: 0 !important;
            background: white !important;
        }

        .receipt-half-layout {
            display: flex !important;
            flex-direction: column !important;
            height: 100% !important;
            width: 100% !important;
            gap: 1mm !important;
            overflow: hidden !important;
        }

        .receipt-half-main {
            flex: 1 1 auto !important;
            min-height: 0 !important;
            overflow: hidden !important;
        }
    }
`;

const injectReceiptPrintStyles = (iframeDoc) => {
    if (!iframeDoc || iframeDoc.getElementById('receipt-print-page-style')) return;
    const style = iframeDoc.createElement('style');
    style.id = 'receipt-print-page-style';
    style.textContent = RECEIPT_PRINT_PAGE_STYLE;
    iframeDoc.head.appendChild(style);
};

export const applyPrintDocumentTitle = (title, iframe) => {
    const safeTitle = (title || 'Fee_Receipt').trim() || 'Fee_Receipt';
    const previousMainTitle = document.title;
    document.title = safeTitle;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) iframeDoc.title = safeTitle;
    return previousMainTitle;
};

export const restorePrintDocumentTitle = (previousTitle) => {
    if (previousTitle != null) document.title = previousTitle;
};

export const executeReceiptPrint = async (iframe, title) => {
    const previousTitle = applyPrintDocumentTitle(title, iframe);
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    injectReceiptPrintStyles(iframeDoc);

    if (iframeDoc?.body) {
        iframeDoc.body.style.margin = '0';
        iframeDoc.body.style.padding = '0';
        iframeDoc.body.style.position = 'relative';
        iframeDoc.body.style.width = '210mm';
        iframeDoc.body.style.height = '297mm';
    }

    try {
        const contentWindow = iframe?.contentWindow;
        if (contentWindow) {
            contentWindow.focus();
            contentWindow.print();
        }
    } finally {
        setTimeout(() => restorePrintDocumentTitle(previousTitle), 2000);
    }
};

// ─── Template-based approach (recommended) ───────────────────────────────────

const _fmtAmt = (amtStr) => {
    const m = String(amtStr).match(/(\d+(\.\d+)?)/);
    if (m) {
        const n = parseFloat(m[0]);
        return String(amtStr).replace(m[0], n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
    return amtStr;
};

const _fmtCur = (num) =>
    (Number(num) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Build a self-contained HTML receipt and print it via a hidden iframe.
 *
 * @param {Object} data
 * @param {string}  data.title          - PDF filename
 * @param {Object}  data.header         - { logo, schoolName, address, contact, email, panNumber }
 * @param {Object}  data.student        - { name, className, rollNo, month }
 * @param {Object}  data.meta           - { billNumber, date, status }
 * @param {Array}   data.lineItems      - [{ label, amount }]
 * @param {number}  data.thisMonthTotal
 * @param {number}  data.duesAmount
 * @param {number}  data.advanceUsed
 * @param {number}  data.balanceDue
 * @param {number}  data.paidAmt
 * @param {number}  data.totalAmount
 * @param {string}  data.remarks
 * @param {string}  data.footer
 */
export const printReceiptFromTemplate = (data) => {
    const {
        title, header, student, meta, lineItems,
        thisMonthTotal, duesAmount, discount, advanceUsed, balanceDue, paidAmt, totalAmount,
        remarks, footer
    } = data;

    // Build table rows
    const itemRows = lineItems.length > 0
        ? lineItems.map((item, i) =>
            `<tr>
                <td style="text-align:center">${i + 1}</td>
                <td>${item.label}</td>
                <td style="text-align:right">${
                    String(item.amount).includes('Rs.') ? _fmtAmt(item.amount) : _fmtCur(parseFloat(item.amount) || 0)
                }</td>
            </tr>`
        ).join('')
        : `<tr><td style="text-align:center">1</td><td>${student.month || 'Fee Payment'}</td><td style="text-align:right">${_fmtCur(thisMonthTotal)}</td></tr>`;

    // Only add empty filler rows when few items (≤3) to give the table visual body
    const fillerCount = Math.max(0, 3 - lineItems.length);
    const fillerRows = Array.from({ length: fillerCount }, () =>
        `<tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>`
    ).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<title>${title || 'Fee Receipt'}</title>
<style>
@page { size: A4 portrait; margin: 0 !important; }
* { box-sizing: border-box; margin: 0; padding: 0; }
html, body {
    width: 210mm; height: 297mm;
    overflow: hidden;
    background: white;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}
.receipt {
    position: absolute; top: 0; left: 0;
    width: 210mm; height: 148.5mm;
    max-height: 148.5mm;
    overflow: hidden;
    padding: 3mm 5mm 2mm 5mm;
    font-family: 'Times New Roman', Times, serif;
    color: #1a1a2e;
    display: flex; flex-direction: column;
}
/* ── Header ── */
.top-row {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 3mm; border-bottom: 1px solid #334155; padding-bottom: 1mm;
    flex-shrink: 0;
}
.school-block { flex: 1; min-width: 0; }
.logo { max-height: 18mm; max-width: 40mm; object-fit: contain; margin-bottom: 0.5mm; }
.school-name { font-size: 16pt; font-weight: 900; color: #1e293b; line-height: 1.15; }
.school-det { font-size: 9pt; color: #334155; line-height: 1.25; }
.stu-block {
    min-width: 50mm; width: 50mm; border: 1px solid #334155;
    padding: 1.5mm 2mm; flex-shrink: 0;
}
.rcpt-title {
    background: #d1d5db; border: 1px solid #334155; border-radius: 6px;
    text-align: center; font-weight: 800; font-size: 9pt;
    padding: 0.5mm 2mm; margin-bottom: 1mm;
}
.stu-f { display: flex; gap: 1mm; font-size: 9pt; line-height: 1.25; border-bottom: 1px dotted #cbd5e1; padding: 0.3mm 0; }
.stu-f:last-child { border-bottom: none; }
.stu-l { font-weight: 700; white-space: nowrap; min-width: 12mm; }
.stu-v { flex: 1; font-weight: 600; }
/* ── Body ── */
.main-row { display: flex; gap: 0; align-items: stretch; flex: 1 1 auto; min-height: 0; overflow: hidden; }
.meta-box {
    border: 1px solid #334155; border-right: none;
    padding: 1mm; width: 22mm; min-width: 22mm;
    display: flex; flex-direction: column; gap: 0.5mm; flex-shrink: 0;
}
.meta-r { display: flex; flex-direction: column; gap: 0.2mm; padding-bottom: 0.5mm; border-bottom: 1px solid #e2e8f0; }
.meta-r:last-child { border-bottom: none; }
.meta-l { font-size: 8pt; font-weight: 800; color: #000; }
.meta-v { font-size: 9pt; font-weight: 700; color: #1e293b; }
.tbl-sec { flex: 1; min-width: 0; }
table.fee { width: 100%; height: 100%; border-collapse: collapse; border: 1px solid #334155; }
table.fee th, table.fee td {
    padding: 0.5mm 1.5mm; border: 1px solid #334155;
    text-align: left; font-size: 9pt; color: #1e293b; line-height: 1.2;
}
table.fee th {
    background: #f1f5f9; font-weight: 700; text-transform: uppercase;
    font-size: 8pt; color: #334155;
}
.sum-box {
    border: 1px solid #334155; border-left: none;
    min-width: 50mm; width: 50mm;
    display: flex; flex-direction: column; justify-content: flex-end; flex-shrink: 0;
}
.sum-r {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.5mm 1.5mm; border-bottom: 1px solid #334155;
    font-size: 8.5pt; gap: 1mm;
}
.sum-r:last-child { border-bottom: none; }
.sum-r.td { background: #f1f5f9; font-weight: 800; }
.sum-l { font-weight: 600; color: #334155; white-space: nowrap; }
.sum-v { font-weight: 700; color: #1e293b; text-align: right; }
/* ── Footer ── */
.rem-box {
    font-size: 9pt; color: #000; border: 1px solid #000;
    padding: 0.8mm 1.5mm; margin-top: 0.5mm; flex-shrink: 0;
}
.sig-row {
    display: flex; justify-content: space-between; align-items: flex-end;
    gap: 4mm; margin-top: auto; padding-top: 2mm; flex-shrink: 0;
}
.sig-box { flex: 1; max-width: 55mm; }
.sig-line { border-top: 1px solid #334155; width: 100%; margin-top: 8mm; }
.sig-txt { font-size: 8pt; font-weight: 700; color: #000; margin-top: 0.5mm; }
.ftr { text-align: center; font-size: 7.5pt; font-weight: 700; color: #000; margin-top: 1mm; flex-shrink: 0; }
</style>
</head>
<body>
<div class="receipt">
    <div class="top-row">
        <div class="school-block">
            ${header.logo ? `<img class="logo" src="${header.logo}" alt="Logo"/>` : ''}
            <div class="school-name">${header.schoolName || 'SCHOOL NAME'}</div>
            <div class="school-det">${header.address || ''}</div>
            <div class="school-det">Tel: ${header.contact || 'N/A'}${header.email ? ` | Email: ${header.email}` : ''}</div>
            ${header.panNumber ? `<div class="school-det"><b>PAN No:</b> ${header.panNumber}</div>` : ''}
        </div>
        <div class="stu-block">
            <div class="rcpt-title">Receipt Bill</div>
            <div class="stu-f"><span class="stu-l">Name :</span><span class="stu-v">${student.name}</span></div>
            <div class="stu-f"><span class="stu-l">Class :</span><span class="stu-v">${student.className}</span></div>
            <div class="stu-f"><span class="stu-l">Roll No. :</span><span class="stu-v">${student.rollNo}</span></div>
            <div class="stu-f"><span class="stu-l">Month :</span><span class="stu-v">${student.month}</span></div>
        </div>
    </div>

    <div class="main-row">
        <div class="meta-box">
            <div class="meta-r"><span class="meta-l">Receipt No.:</span><span class="meta-v">${meta.billNumber}</span></div>
            <div class="meta-r"><span class="meta-l">Date:</span><span class="meta-v">${meta.date}</span></div>
            <div class="meta-r"><span class="meta-l">Status:</span><span class="meta-v">${meta.status}</span></div>
        </div>
        <div class="tbl-sec">
            <table class="fee">
                <thead><tr>
                    <th style="width:35px;text-align:center">S.No</th>
                    <th>Fee Description</th>
                    <th style="width:90px;text-align:right">Amount (Rs.)</th>
                </tr></thead>
                <tbody>${itemRows}${fillerRows}</tbody>
            </table>
        </div>
        <div class="sum-box">
            <div class="sum-r"><span class="sum-l">This month's total :</span><span class="sum-v">${_fmtCur(thisMonthTotal)}</span></div>
            ${duesAmount > 0 ? `<div class="sum-r"><span class="sum-l">Previous balance :</span><span class="sum-v">${_fmtCur(duesAmount)}</span></div>` : ''}
            ${discount > 0 ? `<div class="sum-r"><span class="sum-l">Discount :</span><span class="sum-v">- ${_fmtCur(discount)}</span></div>` : ''}
            ${advanceUsed > 0 ? `<div class="sum-r"><span class="sum-l">Advance used :</span><span class="sum-v">- ${_fmtCur(advanceUsed)}</span></div>` : ''}
            <div class="sum-r td"><span class="sum-l">Total balance due :</span><span class="sum-v">${_fmtCur(balanceDue)}</span></div>
            <div class="sum-r"><span class="sum-l">Paid amount :</span><span class="sum-v">${_fmtCur(paidAmt)}</span></div>
            <div class="sum-r"><span class="sum-l">Bill total :</span><span class="sum-v">${_fmtCur(totalAmount)}</span></div>
        </div>
    </div>

    <div class="rem-box"><b>Remarks :</b> ${remarks || ''}</div>

    <div class="sig-row">
        <div class="sig-box" style="text-align:left"><div class="sig-line"></div><div class="sig-txt">Authorized Signature</div></div>
        <div class="sig-box" style="text-align:right"><div class="sig-line"></div><div class="sig-txt">Student/Parent Signature</div></div>
    </div>

    ${footer ? `<div class="ftr">${footer}</div>` : ''}
</div>
</body>
</html>`;

    // ── Render into hidden iframe and print ──
    let iframe = document.getElementById('receipt-tpl-iframe');
    if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'receipt-tpl-iframe';
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
        document.body.appendChild(iframe);
    }

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    const origTitle = document.title;
    const safeTitle = (title || 'Fee_Receipt').trim();
    document.title = safeTitle;
    if (iframe.contentWindow) iframe.contentWindow.document.title = safeTitle;

    const fire = () => {
        if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        }
        setTimeout(() => { document.title = origTitle; }, 2000);
    };

    // Wait for logo image to load before printing
    const img = doc.querySelector('.logo');
    if (img && !img.complete) {
        img.onload = fire;
        img.onerror = fire;
    } else {
        setTimeout(fire, 200);
    }
};
